import { GetStaticPaths, GetStaticProps } from "next";
import { NextSeo } from "next-seo";
import { Breadcrumbs } from "~/components/Breadcrumbs";
import { Navbar } from "~/components/Navbar";
import { SidebarLayout } from "~/components/SidebarLayout";
import { SOCIAL_OG_IMAGE_URL } from "~/consts";
import { PageTrack } from "~/modules/analytics/components/PageTrack";
import { addBaseUrl } from "~/modules/app/seo/addBaseUrl";
import { BreadcrumbSEO } from "~/modules/app/seo/BreadcrumbSEO";
import {
    MetricIndividualSEO,
    getMetricPageDescription,
    getMetricPageTitle,
} from "~/modules/catalog/seo/MetricIndividualSEO";
import { MeasureIndividualPage } from "~/modules/catalog/pages/measure-individual";
import { UserFetcher } from "~/modules/user/components/UserFetcher";
import { Routes, getMeasureURL } from "~/routes";
import {
    NEXTJS_MISS_REVALIDATE_TIMEOUT,
    NEXTJS_REVALIDATE_TIMEOUT,
} from "~/server/consts";
import { queryMeasures } from "~/modules/measures/api/router";
import type { Measure } from "~/modules/measures/types";
import { DEFAULT_MEASURE_LIST_QUERY } from "~/modules/measures/consts";
import { MeasureColumns } from "~/modules/measures/types";
import { FilterOperators } from "~/modules/filters/types";
import { queryReferenceData } from "~/modules/reference_data/api/router";
import { DEFAULT_REFERENCE_DATA_LIST_QUERY } from "~/modules/reference_data/consts";
import {
    ReferenceDataColumns,
    type ReferenceData,
} from "~/modules/reference_data/types";
import { getTotalAssetCount } from "~/modules/assets/api/getAssetCount";
import { CatalogIndividualLayout } from "~/modules/catalog/components/CatalogIndividualLayout";

interface Props {
    measure: Measure;
    measures: Measure[];
    referenceData: ReferenceData[];
    totalAssetCount: number;
}

export default function MetricPage(props: Props) {
    const { measure, measures, referenceData, totalAssetCount } = props;

    const pageUrl = addBaseUrl(getMeasureURL({ measureSlug: measure.slug }));
    const title = getMetricPageTitle({ measure });
    const description = getMetricPageDescription({ measure });

    return (
        <PageTrack route={Routes.measureIndividual}>
            <NextSeo
                title={title}
                description={description}
                canonical={pageUrl}
                openGraph={{
                    type: "website",
                    url: pageUrl,
                    title,
                    site_name: "RWA.xyz",
                    description,
                    images: [
                        {
                            url: SOCIAL_OG_IMAGE_URL,
                            width: 4000,
                            height: 2300,
                            alt: "rwa.xyz",
                        },
                    ],
                }}
                additionalMetaTags={[
                    {
                        name: "keywords",
                        content: [
                            measure.display_name,
                            "RWA",
                            "real world assets",
                            "tokenized assets",
                            "data catalog",
                            "metric",
                            measure.unit,
                            measure.category,
                        ]
                            .filter(Boolean)
                            .join(", "),
                    },
                ]}
            />
            <MetricIndividualSEO measure={measure} />
            <BreadcrumbSEO
                items={[
                    {
                        name: "Data Catalog",
                        href: Routes.catalog,
                    },
                    {
                        name: measure.display_name,
                        href: getMeasureURL({ measureSlug: measure.slug }),
                    },
                ]}
            />
            <UserFetcher>
                {({ user }) => (
                    <Navbar route={Routes.measureIndividual} user={user} />
                )}
            </UserFetcher>
            <SidebarLayout pathname={Routes.catalog}>
                <Breadcrumbs
                    pages={[
                        {
                            name: "Data Catalog",
                            href: Routes.catalog,
                            current: false,
                        },
                        {
                            name: "Metrics",
                            current: false,
                        },
                        {
                            name: measure.display_name,
                            current: true,
                        },
                    ]}
                />
                <CatalogIndividualLayout
                    measures={measures}
                    referenceData={referenceData}
                    title={measure.display_name}
                    icon={measure.icon}
                    color={measure.color}
                    shortDescription={measure.short_description}
                    description={measure.description}
                >
                    <MeasureIndividualPage
                        measure={measure}
                        totalAssetCount={totalAssetCount}
                    />
                </CatalogIndividualLayout>
            </SidebarLayout>
        </PageTrack>
    );
}

export const getStaticPaths: GetStaticPaths = async () => {
    try {
        // Fetch all measures to generate paths
        const measureListRes = await queryMeasures({
            query: DEFAULT_MEASURE_LIST_QUERY,
        });
        const measures: Measure[] = measureListRes.results;

        if (!measures || measures.length === 0) {
            console.warn("No measures found or empty response received");
            return {
                paths: [],
                fallback: "blocking",
            };
        }

        // Generate paths for all measures
        const paths = measures.map((measure) => ({
            params: {
                metricSlug: measure.slug,
            },
        }));

        return {
            paths,
            fallback: "blocking", // Allow new measures to be generated on first request
        };
    } catch (error) {
        console.error("Failed to generate static paths for measures:", error);
        return {
            paths: [],
            fallback: "blocking",
        };
    }
};

export const getStaticProps: GetStaticProps<Props> = async (ctx) => {
    const metricSlug = ctx.params?.metricSlug as string;

    if (!metricSlug) {
        return { notFound: true, revalidate: NEXTJS_MISS_REVALIDATE_TIMEOUT };
    }

    const [measureRes, allMeasuresRes, referenceDataRes, totalAssetCountRes] =
        await Promise.all([
            queryMeasures({
                query: {
                    ...DEFAULT_MEASURE_LIST_QUERY,
                    filter: {
                        field: MeasureColumns.slug,
                        operator: FilterOperators.Equals,
                        value: metricSlug,
                    },
                },
            }),
            queryMeasures({
                query: {
                    ...DEFAULT_MEASURE_LIST_QUERY,
                    filter: {
                        operator: FilterOperators.And,
                        filters: [
                            {
                                field: MeasureColumns.assetCount,
                                operator: FilterOperators.GreaterThan,
                                value: 0,
                            },
                            {
                                field: MeasureColumns.hidden,
                                operator: FilterOperators.Equals,
                                value: false,
                            },
                        ],
                    },
                },
            }),
            queryReferenceData({
                query: {
                    ...DEFAULT_REFERENCE_DATA_LIST_QUERY,
                    filter: {
                        operator: FilterOperators.And,
                        filters: [
                            {
                                field: ReferenceDataColumns.assetCount,
                                operator: FilterOperators.GreaterThan,
                                value: 0,
                            },
                            {
                                field: ReferenceDataColumns.hidden,
                                operator: FilterOperators.Equals,
                                value: false,
                            },
                        ],
                    },
                },
            }),
            getTotalAssetCount(),
        ]);

    const measure = measureRes.results[0];

    if (!measure) {
        console.warn(
            `Measure not found for slug "${metricSlug}". Redirecting to catalog.`,
        );
        return {
            redirect: {
                permanent: false,
                destination: Routes.catalog,
            },
            revalidate: NEXTJS_MISS_REVALIDATE_TIMEOUT,
        };
    }

    const measures = (allMeasuresRes.results || []).filter(
        (m) => m.asset_count > 0,
    );
    const referenceData = referenceDataRes.results || [];

    return {
        props: {
            measure,
            measures,
            referenceData,
            totalAssetCount: totalAssetCountRes.count,
        },
        revalidate: NEXTJS_REVALIDATE_TIMEOUT,
    };
};
